import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { LOGO, LOGO_WHITE, CONTACT } from "../data/content";

const NAV = [
  {
    label: "Unternehmen",
    to: "/unternehmen",
    children: [
      { label: "Über uns", to: "/unternehmen", testid: "ueber-uns" },
      { label: "Ausstellung", to: "/unternehmen/ausstellung", testid: "ausstellung" },
      { label: "50 Jahre", to: "/unternehmen/50-jahre", testid: "50-jahre" },
      { label: "Auszeichnungen", to: "/unternehmen/auszeichnungen", testid: "auszeichnungen" },
    ],
  },
  { label: "Produkte", to: "/produkte" },
  { label: "Leistungen", to: "/leistungen" },
  // Karriere bleibt technisch Unterseite von Unternehmen (/unternehmen/karriere),
  // ist aber als eigener Hauptmenüpunkt schneller erreichbar.
  { label: "Karriere", to: "/unternehmen/karriere" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = !scrolled; // light (white) content over hero

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-[#E5E0D8] shadow-[0_8px_30px_-20px_rgba(0,0,0,.3)]" : "bg-transparent"
      }`}
    >
      <div className="container-blum flex items-center justify-between h-24 lg:h-28">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src={light ? LOGO_WHITE : LOGO} alt="Fussboden Blum" className="h-16 lg:h-20 w-auto" />
        </Link>

        <nav className={`hidden lg:flex items-center gap-9 ${light ? "text-white" : "text-[#2B2B30]"}`}>
          {NAV.map((n) => {
            const isActive = n.children
              ? pathname.startsWith(n.to) && pathname !== "/unternehmen/karriere"
              : n.to === pathname;
            if (!n.children) {
              return (
                <Link
                  key={n.label}
                  to={n.to}
                  data-testid={`nav-link-${n.label.toLowerCase()}`}
                  className={`nav-link ${isActive ? "active" : ""}`}
                >
                  {n.label}
                </Link>
              );
            }
            return (
              <div key={n.label} className="relative group">
                <Link
                  to={n.to}
                  data-testid={`nav-link-${n.label.toLowerCase()}`}
                  aria-haspopup="true"
                  className={`nav-link flex items-center gap-1.5 ${isActive ? "active" : ""}`}
                >
                  {n.label}
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" aria-hidden="true" />
                </Link>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-5 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200">
                  <div
                    data-testid="nav-dropdown-unternehmen"
                    className="bg-white border border-[#E5E0D8] shadow-[0_24px_50px_-20px_rgba(0,0,0,.35)] py-2 min-w-[230px]"
                  >
                    {n.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-dropdown-${c.testid}`}
                        className={`block px-6 py-3 font-display text-base transition-colors ${
                          pathname === c.to
                            ? "text-[#D32F2F] bg-[#F9F8F6]"
                            : "text-[#2B2B30] hover:text-[#D32F2F] hover:bg-[#F9F8F6]"
                        }`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <a href={CONTACT.phoneHref} data-testid="nav-phone" className={`flex items-center font-medium font-display text-base ${light ? "text-white" : "text-[#2B2B30]"}`}>
            {CONTACT.phone}
          </a>
          <a href="/#kontakt" data-testid="nav-cta" className="btn-base btn-red">Termin anfragen</a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden w-12 h-12 flex items-center justify-center transition-colors ${
            light ? "text-white bg-white/10 border border-white/30" : "text-[#2B2B30] bg-white/70 backdrop-blur border border-[#E5E0D8]"
          }`}
          aria-label="Menü"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-white border-t border-[#E5E0D8]">
          <div className="container-blum py-6 flex flex-col gap-4">
            {NAV.map((n) =>
              !n.children ? (
                <Link
                  key={n.label}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                  className="font-display text-xl text-[#2B2B30] py-1"
                >
                  {n.label}
                </Link>
              ) : (
                <div key={n.label}>
                  <div className="flex items-center justify-between gap-2">
                    <Link
                      to={n.to}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                      className="font-display text-xl text-[#2B2B30] py-1"
                    >
                      {n.label}
                    </Link>
                    <button
                      onClick={() => setSubOpen((o) => !o)}
                      data-testid="nav-mobile-unternehmen-toggle"
                      aria-label="Untermenü Unternehmen öffnen"
                      aria-expanded={subOpen}
                      className="w-10 h-10 flex items-center justify-center text-[#2B2B30]"
                    >
                      <ChevronDown className={`w-6 h-6 transition-transform duration-200 ${subOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {subOpen && (
                    <div data-testid="nav-mobile-unternehmen-submenu" className="flex flex-col gap-3 pl-4 mt-3 border-l-2 border-[#E5E0D8]">
                      {n.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          data-testid={`nav-mobile-${c.testid}`}
                          className="font-display text-lg text-[#6B6B73] py-0.5"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
            <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-[#D32F2F] font-display font-semibold mt-2 text-lg">
              <Phone className="w-5 h-5" /> {CONTACT.phone}
            </a>
            <a href="/#kontakt" onClick={() => setOpen(false)} className="btn-base btn-red w-full mt-2">Termin anfragen</a>
          </div>
        </div>
      )}
    </header>
  );
};
